/* eslint-disable no-unsafe-optional-chaining */
import styled from '@emotion/styled'
import { PageOptions } from '@graphcommerce/framer-next-pages'
import { hygraphPageContent } from '@graphcommerce/graphcms-ui'
import { mergeDeep } from '@graphcommerce/graphql'
import { AmFaqQuestion } from '@graphcommerce/graphql-mesh'
import { Image } from '@graphcommerce/image'
import {
  AddProductsToCartFormProps,
  ProductPageMeta,
  getProductStaticPaths,
  productPageCategory,
} from '@graphcommerce/magento-product'
import {
  ConfigurableContextProvider,
  defaultConfigurableOptionsSelection,
} from '@graphcommerce/magento-product-configurable'
import { StoreConfigDocument, redirectOrNotFound } from '@graphcommerce/magento-store'
import { GetStaticProps, isTypename } from '@graphcommerce/next-ui'
import { TabContext, TabPanel } from '@mui/lab'
import { Chip, NoSsr, Paper, Rating, Tab, Tabs } from '@mui/material'
import { GetStaticPaths } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import fastShipping from '../../assets/images/fast-shipping.svg'
import shipping from '../../assets/images/shipping.svg'
import { LayoutNavigation, LayoutNavigationProps } from '../../components'
import { FourOverConfProductAddToCart } from '../../components/4Over/FourOverConfProductAddToCart'
import { FourOverProductAddToCart } from '../../components/4Over/FourOverProductAddToCart'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { RelatedProducts } from '../../components/Home/RelatedProducts'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { JsonLd } from '../../components/NextUi/JsonLd/JsonLd'
import {
  jsonLdProduct,
  jsonLdProductOffer,
} from '../../components/Product/JsonLdProduct/jsonLdProduct'
import { OrderProcess } from '../../components/Product/OrderProcess'
import { PrintTemplates } from '../../components/Product/PrintTemplates/PrintTemplates'
import { ProductPageGallery } from '../../components/Product/ProductPageGallery'
import { ProductSpecs } from '../../components/Product/ProductSpecs/ProductSpecs'
import { ConfigurableProductAddToCart } from '../../components/ProductConfigurable/ConfigurableProductAddToCart'
import { ProductQuestionsAndAnswers } from '../../components/Reviews/ProductQuestionsAndAnswers'
import { ProductsReviewsQATabs } from '../../components/Reviews/ProductsReviewsQATabs'
import { ProductPage2Document, ProductPage2Query } from '../../graphql/ProductPage2.gql'
import useCallAPI from '../../hooks/useCallAPI'
import { useResetCheckout } from '../../hooks/useResetCheckout'
import useWindowSize from '../../hooks/useWindowSize'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'
import templiLogo from '../../public/templi-logo.svg'

type Props = ProductPage2Query & Pick<AddProductsToCartFormProps, 'defaultValues'> & AmFaqQuestion

type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

export interface StyledTabsProps {
  children?: React.ReactNode
  value: string
  onChange: (event: React.SyntheticEvent, newValue: string) => void
  variant?: 'standard' | 'scrollable' | 'fullWidth' | undefined
  scrollButtons?: boolean
  allowScrollButtonsMobile?: boolean
}

export const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs {...props} TabIndicatorProps={{ children: <span className='MuiTabs-indicatorSpan' /> }} />
))(({ theme }) => ({
  color: '#6A6A6A',
  minHeight: 36,
  height: 36,
  marginLeft: '10%',
  marginRight: '10%',
  borderBottom: '1px solid #D1D3D4',
  // [(theme as any).breakpoints.up('sm')]: {
  //   marginLeft: '10%',
  //   marginRight: 0,
  // },
  [(theme as any).breakpoints.up('md')]: {
    marginRight: '10%',
    marginLeft: 0,
  },
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    height: 4,
  },
  '& .MuiTabs-indicatorSpan': {
    backgroundColor: 'none',
  },
}))

export interface StyledTabProps {
  label: string
  id: string
  value: string
  'aria-controls': string
  onHover?: () => void
}

export const StyledTab = styled((props: StyledTabProps) => (
  <Tab disableRipple {...props} onMouseEnter={props.onHover} />
))(({ theme }) => ({
  minWidth: 0,
  minHeight: 36,
  height: 36,
  paddingRight: 10,
  paddingLeft: 10,
  marginRight: '40px',
  textAlign: 'center',
  textTransform: 'none',
  // fontWeight: theme.typography.fontWeightRegular,
  color: '#6A6A6A',
  fontSize: 16,
  fontWeight: 500,
  [(theme as any).breakpoints.up('md')]: {
    fontSize: 20,
  },

  ':hover': {
    color: '#000000',
  },
  '&.Mui-selected': {
    color: '#2196F3',
    borderBottom: '3px solid #2196F3',
    fontWeight: 'bold',
    ':hover': {
      color: '#0069D9',
    },
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#983FF1',
  },
}))

export interface StyledTabPanelProps {
  value: string
  children?: React.ReactNode
}

export const StyledTabPanel = styled((props: StyledTabPanelProps) => <TabPanel {...props} />)(
  ({ theme }) => ({
    minHeight: 400,
    '&.MuiTabPanel-root': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  }),
)
function ProductPage(props: Props) {
  const { products, relatedUpsells } = props

  useResetCheckout()

  const product = mergeDeep(products, relatedUpsells)?.items?.[0]

  const { isMobile } = useWindowSize()

  const [bottomLineData, setBottomLineData] = useState<any>()
  const router = useRouter()
  const [value, setValue] = useState('0')

  const { apiCall: reviewsBottomLineDataApiCall } = useCallAPI({
    url: `${process.env.NEXT_PUBLIC_YOTPO_BASE_URL}/products/${process.env.NEXT_PUBLIC_YOTPO_APP_KEY}/${product?.id}/bottomline`,
    contentType: 'application/json',
    method: 'GET',
  })

  const printTemplatesSectionRef = useRef<HTMLDivElement>(null)
  const reviewsSectionRef = useRef(null)
  const handleScrollToReviews = () => {
    window.scrollTo({
      // eslint-disable-next-line no-unsafe-optional-chaining
      top: (reviewsSectionRef?.current as any)?.offsetTop - 70,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    const fetchBottomLineData = async () => {
      const res = await reviewsBottomLineDataApiCall()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setBottomLineData(res?.data?.response?.bottomline)
    }
    fetchBottomLineData().catch((e) => {
      // addtional error handling here, the error must be set by the useCallAPI hook already
      console.error(e)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id])

  useEffect(() => {
    if (router?.asPath?.endsWith('scrollToReviews')) {
      handleScrollToReviews()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, reviewsSectionRef.current])

  if (!product?.sku || !product.url_key) return null
  const localPath = templiLogo?.src?.replace('/', '')

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue)
  }
  const a11yProps = (index: number) => ({
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  })

  const productDescription = product?.description?.html?.substring(
    product?.description?.html.indexOf('<p>') + 3,
    product?.description?.html.indexOf('</p>'),
  )

  const numberOfDaysInWeek = 5
  const weeksBase = Math.floor((product.production_time ?? 0) / numberOfDaysInWeek)
  const weeksDisplay = product.production_time === numberOfDaysInWeek ? 'week' : 'weeks'
  const numberOfWeeksDisplay =
    (product.production_time ?? 0) % numberOfDaysInWeek > 0
      ? `${weeksBase}-${weeksBase + 1}`
      : weeksBase
  const productionTimeDisplay =
    product.production_time && product.production_time < numberOfDaysInWeek
      ? `${product.production_time} days`
      : `${numberOfWeeksDisplay} ${weeksDisplay}`

  const shippingIconDisplay =
    product.production_time && product.production_time < numberOfDaysInWeek
      ? fastShipping
      : shipping

  const setTabToSpecsAndTemplatesAndScroll = () => {
    setValue('1')
    window.scrollTo({
      // eslint-disable-next-line no-unsafe-optional-chaining
      top: (printTemplatesSectionRef as any)?.current?.offsetTop - 70,
      behavior: 'smooth',
    })
  }

  const hasReviews: boolean = bottomLineData?.total_reviews > 0
  return (
    <div className='m-auto px-[10px] lg:max-w-maxContentWidth lg:px-[60px] lg:pt-[5px] 2xl:px-0'>
      <JsonLd
        identifier='product-page'
        item={{
          '@context': 'https://schema.org',
          ...jsonLdProduct(product),
          ...jsonLdProductOffer(product),
        }}
      />
      {isTypename(product, ['SimpleProduct']) && (
        <div>
          <ProductPageMeta {...product} />
          <Breadcrumbs product={product} />
          <div className='grid-cols-[65fr_35fr] gap-[40px] lg:grid'>
            <div className='mb-[20px] md:mb-0'>
              {isMobile && (
                <div className='my-[10px]'>
                  <h3 className='Type-XXXL-Bold'>{product.name}</h3>
                </div>
              )}
              <ProductPageGallery
                {...product}
                // Do not show small image on PDP. It will be used in product cards
                media_gallery={
                  product.media_gallery?.length && product.media_gallery?.length > 1
                    ? product.media_gallery?.filter(
                        (item) => item?.url && item?.url !== product?.small_image?.url,
                      )
                    : product.media_gallery
                }
                isMobile={isMobile}
              />
            </div>
            <Paper elevation={5} className='h-fit py-5 md:py-0'>
              <div className='mx-10 mt-0 md:my-5'>
                {!isMobile && <h1 className='Type-XXXL-Bold'>{product.name}</h1>}
                <div className='flex items-baseline justify-between'>
                  {product.production_time && (
                    <div className='mt-4 flex items-center justify-center'>
                      <Image src={shippingIconDisplay} unoptimized height={12} className='mr-1' />
                      <span className='Type-XL-Regular whitespace-nowrap pt-1 text-70-grey'>
                        {productionTimeDisplay}
                      </span>
                    </div>
                  )}
                  {hasReviews && (
                    <button
                      type='button'
                      onClick={handleScrollToReviews}
                      className='mt-[16px] flex items-center'
                    >
                      <Rating
                        name='total-ratings'
                        defaultValue={bottomLineData?.average_score}
                        size='small'
                        readOnly
                        precision={0.1}
                        className='scale-90 px-2 align-bottom'
                      />
                      <span className='Type-XL-Bold text-70-grey'>
                        ({bottomLineData?.total_reviews})
                      </span>
                    </button>
                  )}
                </div>
                <FourOverProductAddToCart
                  variables={{ sku: product.sku ?? '' }}
                  name={product.name ?? ''}
                  product={product}
                  callback={setTabToSpecsAndTemplatesAndScroll}
                />
              </div>
            </Paper>
          </div>
        </div>
      )}
      {isTypename(product, ['ConfigurableProduct']) && (
        <ConfigurableContextProvider {...product} sku={product.sku}>
          <ProductPageMeta {...product} />
          <Breadcrumbs product={product} />
          <div className='grid-cols-[50fr_50fr] gap-[40px] lg:grid'>
            <div className='mb-[20px] md:mb-0'>
              {isMobile && (
                <div className='my-[10px]'>
                  <h3 className='Type-XXXL-Bold ml-3 md:ml-0'>{product.name}</h3>
                </div>
              )}
              <ProductPageGallery
                {...product}
                // Do not show small image on PDP. It will be used in product cards
                media_gallery={
                  product.media_gallery?.length && product.media_gallery?.length > 1
                    ? product.media_gallery?.filter(
                        (item) => item?.url && item?.url !== product?.small_image?.url,
                      )
                    : product.media_gallery
                }
                isMobile={isMobile}
              />
            </div>
            <Paper elevation={5} className='h-fit py-5 md:py-5 lg:py-0'>
              <div className='mx-10 mt-0 md:my-5 md:px-[2%] lg:px-[2%] lg:py-[10px] xl:px-[60px] xl:py-5'>
                {!isMobile && <h1 className='Type-XXXL-Bold'>{product.name}</h1>}
                <div className='flex items-baseline justify-between'>
                  {product.production_time && !product.sku.includes('4over') && (
                    <div className='mt-4 flex items-center justify-center'>
                      <Image
                        src={shippingIconDisplay}
                        unoptimized
                        height={12}
                        className='mr-1 pb-[1px]'
                      />
                      <span className='Type-XL-Regular whitespace-nowrap text-70-grey'>
                        {productionTimeDisplay}
                      </span>
                    </div>
                  )}
                  {product?.free_shipping && (
                    <span className='Type-Large-Medium ml-3 whitespace-nowrap text-[#248721]'>
                      FREE SHIPPING
                    </span>
                  )}
                  {hasReviews && (
                    <button
                      type='button'
                      onClick={handleScrollToReviews}
                      className='mt-4 flex items-center'
                    >
                      <Rating
                        name='total-ratings'
                        defaultValue={bottomLineData?.average_score}
                        size='small'
                        readOnly
                        precision={0.1}
                        className='scale-90 px-2 align-bottom'
                      />
                      <span className='Type-XL-Bold text-70-grey'>
                        ({bottomLineData?.total_reviews})
                      </span>
                    </button>
                  )}
                </div>
                {product?.sku?.includes('4over') ? (
                  <FourOverConfProductAddToCart
                    variables={{ sku: product.sku ?? '' }}
                    name={product.name ?? ''}
                    product={product}
                    callback={setTabToSpecsAndTemplatesAndScroll}
                  />
                ) : (
                  <ConfigurableProductAddToCart
                    variables={{ sku: product.sku ?? '', quantity: 1 }}
                    name={product.name ?? ''}
                    product={product}
                    callback={setTabToSpecsAndTemplatesAndScroll}
                  />
                )}
              </div>
            </Paper>
          </div>
        </ConfigurableContextProvider>
      )}
      <Paper className='my-5 flex w-full items-center justify-center' elevation={3}>
        <div className='mx-[40px] my-[20px] w-full' ref={printTemplatesSectionRef}>
          <TabContext value={value}>
            <StyledTabs
              value={value}
              onChange={handleChange}
              variant='scrollable'
              scrollButtons
              allowScrollButtonsMobile
            >
              <StyledTab
                label='DESCRIPTION'
                {...a11yProps(0)}
                value='0'
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              />
              <StyledTab
                label='SPECS & TEMPLATES'
                {...a11yProps(1)}
                value='1'
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              />
              <StyledTab
                label={`FAQ'S`}
                {...a11yProps(2)}
                value='2'
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              />
            </StyledTabs>
            <StyledTabPanel value='0'>
              <div className='mx-[40px] my-[20px]'>
                <span className='Type-XL-Regular'>{productDescription}</span>
              </div>
            </StyledTabPanel>
            <StyledTabPanel value='1'>
              <div className='m-[20px] flex flex-col gap-y-5 md:grid md:grid-cols-2'>
                <ProductSpecs aggregations={products?.aggregations} product={product} />
                <PrintTemplates amfiles={product.amfiles} />
              </div>
            </StyledTabPanel>
            <StyledTabPanel value='2'>
              <div className='mt-6'>
                <ProductQuestionsAndAnswers
                  productId={product.id}
                  productTitle={product.name}
                  productUrl={product.url_key}
                  qaData={product.amfaq_questions ?? []}
                />
              </div>
            </StyledTabPanel>
          </TabContext>
        </div>
      </Paper>
      {product?.requires_artwork && (
        <div className='mb-10'>
          <OrderProcess />
        </div>
      )}
      {/*
      <NoSsr>
        <ProductsReviewsQATabs
          productId={product.id}
          productTitle={product.name}
          productUrl={product.url_key}
          sectionRef={reviewsSectionRef}
          productDescription={product.description}
          productImages={product.media_gallery ?? []}
          bottomLineData={bottomLineData}
          qaData={product.amfaq_questions ?? []}
        />
      </NoSsr> */}
      {/* <RelatedProducts className='mb-10' noHorizontalMargins product_uid={product?.uid} /> */}
    </div>
  )
}

ProductPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default ProductPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  if (import.meta.graphCommerce.legacyProductRoute) return { paths: [], fallback: false }
  if (process.env.NODE_ENV === 'development' || process.env.SKIP_ISG)
    return { paths: [], fallback: 'blocking' }

  const path = (locale: string) => getProductStaticPaths(graphqlSsrClient(locale), locale)
  const paths = (await Promise.all(locales.map(path))).flat(1)

  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  if (import.meta.graphCommerce.legacyProductRoute) return { notFound: true }

  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)

  const urlKey = params?.url ?? '??'

  const conf = client.query({ query: StoreConfigDocument })
  const productPage = staticClient.query({ query: ProductPage2Document, variables: { urlKey } })
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  const product = productPage.then((pp) =>
    pp.data.products?.items?.find((p) => p?.url_key === urlKey),
  )

  const pages = hygraphPageContent(staticClient, 'product/global', product, true)
  if (!(await product)) return redirectOrNotFound(staticClient, conf, params, locale)

  const category = productPageCategory(await product)
  const up =
    category?.url_path && category?.name
      ? { href: `/${category.url_path}`, title: category.name }
      : { href: `/`, title: 'Home' }

  return {
    props: {
      ...defaultConfigurableOptionsSelection(urlKey, client, (await productPage).data),
      ...(await layout).data,
      ...(await pages).data,
      apolloState: await conf.then(() => client.cache.extract()),
      up,
    },
    revalidate: process.env.SKIP_ISG ? 1 : 60 * 5,
  }
}
